/**
 * Inline Customer Auto-Suggest Module
 * Provides auto-suggest functionality for customer input fields
 */

let searchTimeouts = {};
let selectedCustomer = null;

/**
 * Get API base URL
 */
function getApiBaseUrl() {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:3001/api'
        : '/api';
}

/**
 * Initialize auto-suggest on an input field
 * @param {string} inputId - The input element ID
 * @param {string} searchField - Field to search ('name', 'email', or 'boat_name')
 */
export function initAutoSuggest(inputId, searchField) {
    const input = document.getElementById(inputId);
    if (!input) {
        console.warn(`Input field ${inputId} not found`);
        return;
    }

    // Create dropdown container
    const dropdown = document.createElement('div');
    dropdown.id = `${inputId}-suggestions`;
    dropdown.className = 'customer-suggestions-dropdown';
    dropdown.style.cssText = `
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 5px 5px;
        max-height: 300px;
        overflow-y: auto;
        display: none;
        z-index: 1000;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    `;

    // Make parent position relative
    const parent = input.parentElement;
    if (parent.style.position !== 'relative' && parent.style.position !== 'absolute') {
        parent.style.position = 'relative';
    }
    parent.appendChild(dropdown);

    // Add search icon
    const searchIcon = document.createElement('span');
    searchIcon.className = 'search-icon-indicator';
    searchIcon.textContent = '🔍';
    searchIcon.style.cssText = `
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        opacity: 0.5;
        font-size: 14px;
    `;
    parent.appendChild(searchIcon);

    // Input event handler
    input.addEventListener('input', async (e) => {
        const query = e.target.value.trim();

        // Clear existing timeout
        if (searchTimeouts[inputId]) {
            clearTimeout(searchTimeouts[inputId]);
        }

        // Hide dropdown if query too short
        if (query.length < 2) {
            dropdown.style.display = 'none';
            return;
        }

        // Show loading state
        dropdown.innerHTML = '<div style="padding: 12px; text-align: center; color: #999;">Searching...</div>';
        dropdown.style.display = 'block';

        // Debounce search
        searchTimeouts[inputId] = setTimeout(async () => {
            try {
                const API_BASE_URL = getApiBaseUrl();
                const response = await fetch(`${API_BASE_URL}/search-customers-with-boats?search=${encodeURIComponent(query)}`);
                const customers = await response.json();

                if (customers && customers.length > 0) {
                    displaySuggestions(customers, dropdown, inputId);
                } else {
                    dropdown.innerHTML = '<div style="padding: 12px; text-align: center; color: #999;">No customers found</div>';
                }
            } catch (error) {
                console.error('Error searching customers:', error);
                dropdown.innerHTML = '<div style="padding: 12px; text-align: center; color: #d32f2f;">Search error</div>';
            }
        }, 300);
    });

    // Focus handler - show suggestions if we have them
    input.addEventListener('focus', () => {
        if (dropdown.innerHTML && input.value.length >= 2) {
            dropdown.style.display = 'block';
        }
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
        if (!parent.contains(e.target)) {
            dropdown.style.display = 'none';
        }
    });

    console.log(`✅ Auto-suggest initialized for ${inputId}`);
}

/**
 * Display customer suggestions in dropdown
 */
function displaySuggestions(customers, dropdown, inputId) {
    const html = customers.map(customer => {
        const boatName = customer.boat_name || '';
        const boatLength = customer.boat_length || '';
        const email = customer.email || '';
        const hasPaymentMethod = customer.has_payment_method || false;

        // Build boat display string
        let boatDisplay = '';
        if (boatName && boatLength) {
            boatDisplay = ` → ${boatName} (${boatLength}ft)`;
        } else if (boatName) {
            boatDisplay = ` → ${boatName}`;
        } else if (boatLength) {
            boatDisplay = ` → ${boatLength}ft boat`;
        }

        // Payment method indicator
        const paymentIndicator = hasPaymentMethod
            ? '<span style="color: #27ae60; font-size: 16px; margin-left: 8px;" title="Has payment method on file">💳✓</span>'
            : '<span style="color: #95a5a6; font-size: 16px; margin-left: 8px;" title="No payment method on file">⚠️</span>';

        return `
            <div class="suggestion-item" onclick="window.__selectInlineCustomer(${JSON.stringify(customer).replace(/"/g, '&quot;')}, '${inputId}')"
                 style="padding: 12px 15px; cursor: pointer; border-bottom: 1px solid #f0f0f0; transition: background 0.15s;">
                <div style="font-weight: 600; color: #2c3e50; margin-bottom: 4px; display: flex; align-items: center; justify-content: space-between;">
                    <span>${customer.name}${boatDisplay}</span>
                    ${paymentIndicator}
                </div>
                <div style="font-size: 13px; color: #7f8c8d;">
                    📧 ${email}
                </div>
            </div>
        `;
    }).join('');

    dropdown.innerHTML = html;
    dropdown.style.display = 'block';

    // Add hover effect
    const items = dropdown.querySelectorAll('.suggestion-item');
    items.forEach(item => {
        item.addEventListener('mouseenter', () => {
            item.style.background = '#f8f9fa';
        });
        item.addEventListener('mouseleave', () => {
            item.style.background = 'white';
        });
    });
}

/**
 * Select a customer and fill all fields
 */
export function selectInlineCustomer(customer, triggeredFromInputId) {
    console.log('📝 Selected customer:', customer);

    // Store selected customer
    selectedCustomer = customer;
    window.selectedCustomer = customer;
    if (window.adminApp) {
        window.adminApp.selectedCustomer = customer;
    }

    // Fill customer fields
    const nameInput = document.getElementById('wizardCustomerName');
    const emailInput = document.getElementById('wizardCustomerEmail');
    const phoneInput = document.getElementById('wizardCustomerPhone');
    const boatNameInput = document.getElementById('wizardBoatName');
    const boatLengthInput = document.getElementById('wizardBoatLength');

    if (nameInput && customer.name) nameInput.value = customer.name;
    if (emailInput && customer.email) emailInput.value = customer.email;
    if (phoneInput && customer.phone) phoneInput.value = customer.phone;
    if (boatNameInput && customer.boat_name) boatNameInput.value = customer.boat_name;
    if (boatLengthInput && customer.boat_length) boatLengthInput.value = customer.boat_length;

    // Store boat data if available
    if (customer.boat_id) {
        window.selectedBoat = {
            id: customer.boat_id,
            name: customer.boat_name,
            length: customer.boat_length,
            make: customer.boat_make,
            model: customer.boat_model,
            type: customer.boat_type,
            hull_type: customer.hull_type,
            has_twin_engines: customer.has_twin_engines,
            marina: customer.marina,
            dock: customer.dock,
            slip: customer.slip
        };

        // Load saved anode assignments for this boat
        if (typeof window.loadBoatAnodes === 'function') {
            window.loadBoatAnodes(customer.boat_id);
        }

        // Load historical service conditions for paint, growth, propellers
        if (typeof window.loadHistoricalConditions === 'function') {
            window.loadHistoricalConditions();
        }
    }

    // Add visual indicator that customer is selected
    addSelectedIndicators();

    // Hide all suggestion dropdowns
    document.querySelectorAll('.customer-suggestions-dropdown').forEach(dd => {
        dd.style.display = 'none';
    });

    // Update charge summary
    if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
        window.updateChargeSummary();
    }

    // Update wizard pricing
    if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
        window.updateWizardPricing();
    }

    console.log('✅ Customer fields auto-filled');
}

/**
 * Add visual indicators that customer is selected
 */
function addSelectedIndicators() {
    const fields = ['wizardCustomerName', 'wizardCustomerEmail', 'wizardBoatName'];

    fields.forEach(fieldId => {
        const input = document.getElementById(fieldId);
        if (input && input.value) {
            input.style.borderColor = '#4caf50';
            input.style.background = '#f1f8f4';
        }
    });

    // Add clear button if it doesn't exist
    if (!document.getElementById('clearCustomerSelection')) {
        const nameInput = document.getElementById('wizardCustomerName');
        if (nameInput) {
            const clearBtn = document.createElement('button');
            clearBtn.id = 'clearCustomerSelection';
            clearBtn.type = 'button';
            clearBtn.textContent = '× Clear Customer';
            clearBtn.style.cssText = `
                margin-top: 10px;
                padding: 8px 16px;
                background: #e0e0e0;
                border: 1px solid #bdbdbd;
                border-radius: 5px;
                cursor: pointer;
                font-size: 14px;
                transition: background 0.2s;
            `;
            clearBtn.onclick = clearCustomerSelection;

            clearBtn.addEventListener('mouseenter', () => {
                clearBtn.style.background = '#d0d0d0';
            });
            clearBtn.addEventListener('mouseleave', () => {
                clearBtn.style.background = '#e0e0e0';
            });

            const container = document.getElementById('dynamicBoatConfig');
            if (container) {
                const firstHr = container.querySelector('hr');
                if (firstHr) {
                    firstHr.insertAdjacentElement('beforebegin', clearBtn);
                }
            }
        }
    }
}

/**
 * Clear customer selection and reset fields
 */
export function clearCustomerSelection() {
    selectedCustomer = null;
    window.selectedCustomer = null;
    window.selectedBoat = null;
    if (window.adminApp) {
        window.adminApp.selectedCustomer = null;
    }

    // Clear field values (except boat length which might be manually entered)
    const nameInput = document.getElementById('wizardCustomerName');
    const emailInput = document.getElementById('wizardCustomerEmail');
    const phoneInput = document.getElementById('wizardCustomerPhone');
    const boatNameInput = document.getElementById('wizardBoatName');

    if (nameInput) {
        nameInput.value = '';
        nameInput.style.borderColor = '';
        nameInput.style.background = '';
    }
    if (emailInput) {
        emailInput.value = '';
        emailInput.style.borderColor = '';
        emailInput.style.background = '';
    }
    if (phoneInput) {
        phoneInput.value = '';
    }
    if (boatNameInput) {
        boatNameInput.value = '';
        boatNameInput.style.borderColor = '';
        boatNameInput.style.background = '';
    }

    // Remove clear button
    const clearBtn = document.getElementById('clearCustomerSelection');
    if (clearBtn) {
        clearBtn.remove();
    }

    // Clear anodes
    if (window.anodeAssignments) {
        window.anodeAssignments = {};
        if (typeof window.updateAnodeAssignmentsList === 'function') {
            window.updateAnodeAssignmentsList();
        }
    }

    console.log('✅ Customer selection cleared');
}

// Expose to window for onclick handlers
window.__selectInlineCustomer = selectInlineCustomer;
window.__clearCustomerSelection = clearCustomerSelection;
